import { aplicarTema, ouvirSistema, salvarTema, temaEfetivo, temaSalvo } from '../lib/tema';
import React, { useEffect, useRef } from 'react';

import { getDateStatus } from '../lib/formato';

function Spinner() {
  return <div className="flex items-center justify-center p-10">
    <div className="w-7 h-7 border-2 border-blue-500 border-t-transparent rounded-full animate-spin opacity-70"/>
  </div>;
}

function StatCard({ label, value, color='text-gray-900', sub }) {
  return <div className="acard rounded-2xl p-4 flex flex-col gap-1 hover:shadow-sm transition-shadow">
    <div className="t-caption-2 text-gray-400 sf-caption font-semibold">{label}</div>
    <div className={`text-2xl font-bold tabular ${color}`}>{value}</div>
    {sub && <div className="text-xs text-gray-400">{sub}</div>}
  </div>;
}

function DateBadge({ dateStr, size='sm' }) {
  const st = getDateStatus(dateStr);
  if (!st) return null;
  const icon = st.status==='overdue'?'⚠️':st.status==='today'?'🔔':st.status==='soon'?'📅':'🗓';
  const sz = size==='lg' ? 'text-sm px-3 py-1.5 gap-1.5' : 'text-xs px-2 py-0.5 gap-1';
  return <span className={`inline-flex items-center rounded-full font-medium ${sz} ${st.cls}`}>
    {icon} {st.label}
  </span>;
}

// Modo escuro (roadmap #23): claro → escuro → automático
function TemaToggle() {
  const [escolha, setEscolha] = React.useState(() => temaSalvo());
  React.useEffect(() => ouvirSistema(() => setEscolha(e => e)), []);
  React.useEffect(() => { aplicarTema(escolha); }, [escolha]);
  const proximo = { claro: 'escuro', escuro: 'auto', auto: 'claro' };
  const icone   = { claro: '☀️', escuro: '🌙', auto: '🌗' };
  const rotulo  = { claro: 'Tema claro', escuro: 'Tema escuro', auto: `Automático (${temaEfetivo('auto') === 'escuro' ? 'escuro' : 'claro'})` };
  return <button
    onClick={() => setEscolha(e => { const n = proximo[e] || 'claro'; salvarTema(n); return n; })}
    title={`${rotulo[escolha] || rotulo.auto} — clique para alternar`}
    aria-label={rotulo[escolha] || rotulo.auto}
    className="p-2 rounded-full hover:bg-black/[0.06] transition-colors text-xl leading-none">
    {icone[escolha] || icone.auto}
  </button>;
}

function Modal({ title, onClose, children, wide }) {
  return <div className="glass-overlay fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
    <div className={`animate-sheet acard rounded-3xl shadow-2xl mt-8 mb-10 w-full ${wide?'max-w-2xl':'max-w-md'}`}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06]">
        <h2 className="t-subhead font-semibold text-gray-900 tracking-tight">{title}</h2>
        <button aria-label="Fechar" onClick={onClose} className="tap-safe w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 text-sm font-bold transition-colors">✕</button>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  </div>;
}

// ─── LeadForm ─────────────────────────────────────────────

function Toast({ message, type='success', onDone }) {
  React.useEffect(() => { const t = setTimeout(onDone, 2600); return () => clearTimeout(t); }, []);
  const cls = type==='error' ? 'bg-red-600' : type==='warning' ? 'bg-amber-500' : 'bg-gray-900';
  const icon = type==='error' ? '✕' : type==='warning' ? '⚠️' : '✓';
  return <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] ${cls} text-white px-5 py-2.5 rounded-2xl shadow-2xl text-sm font-semibold animate-fade-in-up flex items-center gap-2 whitespace-nowrap pointer-events-none`}>
    <span>{icon}</span> {message}
  </div>;
}

// ─── GlobalSearchModal ────────────────────────────────────

function NavIcon({ name, className = 'w-[18px] h-[18px]' }) {
  const paths = {
    pipeline:   'M4 5h4v14H4zM10 5h4v9h-4zM16 5h4v5h-4z',
    fechados:   'M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    perdidos:   'M15 9l-6 6M9 9l6 6M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    dashboard:  'M4 19V9M10 19V5M16 19v-6M22 19H2',
    kpis:       'M3 17l5-6 4 4 5-7 4 5M3 21h18',
    mes:        'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4M8 14h2M12 14h2M8 17h2',
    vendedores: 'M16 19v-1a4 4 0 00-4-4H8a4 4 0 00-4 4v1M10 10a3 3 0 100-6 3 3 0 000 6M20 19v-1a4 4 0 00-3-3.87M16 4.13a4 4 0 010 7.75',
    parceiros:  'M3 21V8l6-4 6 4v13M15 21V12l6 3v6M3 21h18M7 12h2M7 16h2',
    tarefas:    'M4 6h2l1.5 1.5L11 4M4 13h2l1.5 1.5L11 11M4 20h2l1.5 1.5L11 18M14 6h6M14 13h6M14 20h6',
    calendario: 'M3 9h18M8 3v4M16 3v4M4 5h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1z',
    concluidas: 'M9 12l2 2 4-4M4 5h16a1 1 0 011 1v13a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1z',
    indicacoes: 'M3 12h5l2 3h4l2-3h5M3 12l2.5-7h13L21 12v6a1 1 0 01-1 1H4a1 1 0 01-1-1z',
    busca:      'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
    historico:  'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    sidebar:    'M4 5h16v14H4zM10 5v14',
    campos:     'M4 7h10M4 12h16M4 17h7M18 5v4M20 7h-4',
    sites:      'M12 3a9 9 0 100 18 9 9 0 000-18zM3.6 9h16.8M3.6 15h16.8M12 3c2.5 2.4 2.5 15.6 0 18M12 3c-2.5 2.4-2.5 15.6 0 18',
    equipe:     'M16 19v-1.5a3.5 3.5 0 00-3.5-3.5h-5A3.5 3.5 0 004 17.5V19M10 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM20 19v-1.5a3.5 3.5 0 00-2.6-3.4M15.5 4.3a3.5 3.5 0 010 6.8',
  };
  const d = paths[name] || paths.pipeline;
  return <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"
    strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={d} />
  </svg>;
}

// ─── C-34: campos visíveis no cartão do kanban ────────────

function Confetti({ onDone }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    const COLORS = ['#34C759','#007AFF','#FF9500','#FF3B30','#BF5AF2','#FFD60A','#30D158','#FF6B35','#00C7BE'];
    const pieces = Array.from({length:140},()=>({
      x: canvas.width * (0.1 + Math.random() * 0.8),
      y: -20 - Math.random() * 180,
      w: 7 + Math.random() * 9,
      h: 4 + Math.random() * 5,
      color: COLORS[Math.floor(Math.random()*COLORS.length)],
      rot: Math.random() * Math.PI * 2,
      rSpeed: (Math.random()-0.5) * 0.14,
      vx: (Math.random()-0.5) * 4,
      vy: 1.5 + Math.random() * 4,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
    }));
    let frame, start = null;
    const draw = ts => {
      if (!start) start = ts;
      const elapsed = ts - start;
      ctx.clearRect(0,0,canvas.width,canvas.height);
      pieces.forEach(p=>{
        p.x += p.vx; p.y += p.vy; p.vy += 0.06; p.rot += p.rSpeed;
        const alpha = elapsed < 2200 ? 1 : Math.max(0, 1-(elapsed-2200)/800);
        ctx.save(); ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y); ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        if (p.shape === 'circle') { ctx.beginPath(); ctx.arc(0,0,p.w/2,0,Math.PI*2); ctx.fill(); }
        else { ctx.fillRect(-p.w/2,-p.h/2,p.w,p.h); }
        ctx.restore();
      });
      if (elapsed < 3000) frame = requestAnimationFrame(draw);
      else onDone?.();
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, []);
  return <canvas ref={ref} style={{position:'fixed',inset:0,pointerEvents:'none',zIndex:9999,width:'100vw',height:'100vh'}} />;
}

// Temperatura do lead: 1=❄️ a 5=🚀

export { TemaToggle, Confetti, DateBadge, Modal, NavIcon, Spinner, StatCard, Toast };
